import { fetchBatchPlacesByIds, fetchPlaceAncestorsById, fetchPlaceByGeoCoords, fetchPlaceById, fetchPlaceByName } from 'actions/PlaceActions'
import { useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useAppSelector, useAppDispatch, shallowEqual } from './reduxHooks'
import useStableObjectReference from './useStableObjectReference'
import { getTypeaheadSuggestions } from 'api/search'
import GeoContext from 'contexts/geoContext'
import debounce from 'debounce-promise'
import { EmptyArray } from 'lib/array/arrayUtils'
import { getPlaceKey } from 'lib/geo/geoUtils'
import { PlaceIdentitiesByPlaceId } from 'constants/places'
import {
  getActiveLocale,
  getPlaceCacheEntriesForIds,
  getResolvedPlace,
} from 'selectors/placeSelectors'
import { getReadyPlaceFromCacheEntry, withIdentityFallback } from 'lib/places/placeCacheUtils'

export interface LocalizedPlaceResult {
  place?: App.Place
  isLoading: boolean
  isUnavailable: boolean
  isMissing?: boolean
}

export function useResolvedPlaceIdentities(identities: ReadonlyArray<App.PlaceIdentity>): Array<App.Place | undefined> {
  const placeIds = useMemo(
    () => identities.map(identity => identity.id),
    [identities],
  )
  const localizedPlaces = useLocalizedPlaces(placeIds)
  return useMemo(
    () => identities.map((identity) => {
      const resolved = localizedPlaces[identity.id]?.place
      if (!resolved) return undefined
      return withIdentityFallback(resolved, identity)
    }),
    [identities, localizedPlaces],
  )
}

export function useResolvedPlaceIdentity(identity?: App.PlaceIdentity): App.Place | undefined {
  const identities = useMemo(() => (identity ? [identity] : EmptyArray), [identity])
  const [resolved] = useResolvedPlaceIdentities(identities)
  return resolved
}

export function useLocalizedPlaces(placeIds: ReadonlyArray<string>): Record<string, LocalizedPlaceResult> {
  const dispatch = useAppDispatch()
  const locale = useAppSelector(getActiveLocale)
  const normalizedIds = useMemo(
    () => Array.from(new Set(placeIds.filter(Boolean))),
    [placeIds],
  )
  const dedupedIds = useStableObjectReference(normalizedIds)

  useEffect(() => {
    if (dedupedIds.length > 0) {
      dispatch(fetchBatchPlacesByIds(dedupedIds))
    }
  }, [dedupedIds, dispatch, locale])

  const cacheEntries = useAppSelector(
    (state) => getPlaceCacheEntriesForIds(state, dedupedIds, locale),
    shallowEqual,
  )

  return useMemo(() => {
    const result: Record<string, LocalizedPlaceResult> = {}
    for (const id of dedupedIds) {
      const entry = cacheEntries[id]
      result[id] = {
        place: getReadyPlaceFromCacheEntry(entry),
        isLoading: !entry || entry.status === 'loading',
        isUnavailable: entry?.status === 'missing' || entry?.status === 'error',
        isMissing: entry?.status === 'missing',
      }
    }
    return result
  }, [cacheEntries, dedupedIds])
}

export function useResolvedPlaceName(placeId?: string): {
  name?: string
  isLoading: boolean
  isUnavailable: boolean
} {
  const ids = useMemo(() => (placeId ? [placeId] : EmptyArray), [placeId])
  const localizedPlaces = useLocalizedPlaces(ids)
  const result = placeId ? localizedPlaces[placeId] : undefined

  return useMemo(() => ({
    name: result?.place?.name,
    isLoading: !!placeId && (result?.isLoading ?? true),
    isUnavailable: result?.isUnavailable ?? false,
  }), [result, placeId])
}

// Shared enrichment for `App.SearchItem` lists. Overrides `format.mainText` and
// `format.secondaryText` from the Place API cache so translated display wins across
// verticals (popular destinations, legacy HTTP typeahead). The socket-streaming path
// uses `getEnrichedTypeaheadResults` for the richer `SearchItemV3` shape.
export function useLocalizedSearchItems<T extends App.SearchItem>(items: ReadonlyArray<T>): Array<T> {
  const placeIds = useMemo(
    () => Array.from(new Set(items
      .filter(item =>
        (item.searchType === 'destination' || item.searchType === 'landmark') &&
        item.value,
      )
      .map(item => item.value))),
    [items],
  )
  const localizedPlaces = useLocalizedPlaces(placeIds)
  return useMemo(
    () => items.map((item) => {
      const localized = item.value ? localizedPlaces[item.value] : undefined
      const place = localized?.place
      if (!place) return item
      return {
        ...item,
        format: {
          ...item.format,
          mainText: place.name,
          secondaryText: place.location ?? item.format.secondaryText,
        },
      }
    }),
    [items, localizedPlaces],
  )
}

export function useLocalizedBreadcrumbs(items: ReadonlyArray<App.BreadcrumbItem>): Array<App.BreadcrumbItem> {
  const placeIds = useMemo(
    () => items.map(item => item.placeId).filter((id): id is string => !!id),
    [items],
  )
  const localizedPlaces = useLocalizedPlaces(placeIds)
  return useMemo(
    () => items.map((item) => {
      const place = item.placeId ? localizedPlaces[item.placeId]?.place : undefined
      return place ? { ...item, text: place.name } : item
    }),
    [items, localizedPlaces],
  )
}

export function usePlaceById(placeId = '', disable: boolean = false): App.Place | undefined {
  const locale = useAppSelector(getActiveLocale)
  const place = useAppSelector(state => getResolvedPlace(state, placeId, locale))
  const dispatch = useAppDispatch()

  useEffect(() => {
    if (!disable && placeId) {
      dispatch(fetchPlaceById(placeId))
    }
  }, [placeId, disable, dispatch, locale])

  const enriched = useMemo(() => {
    if (disable || !place) return undefined
    return withIdentityFallback(place, PlaceIdentitiesByPlaceId[placeId])
  }, [disable, place, placeId])

  return enriched ?? undefined
}

export function usePlaceByCoords(
  latitude?: number,
  longitude?: number,
  levels?: Array<App.PlaceType>,
  disable = false,
): [App.Place | undefined, boolean] {
  const placeKey = getPlaceKey(latitude, longitude, levels)
  const place = useAppSelector(state => state.destination.coordsToPlace[placeKey])
  const fetching = useAppSelector(state =>
    disable ? false : !!state.destination.placesLoading[placeKey],
  )
  const dispatch = useAppDispatch()

  useEffect(() => {
    if (disable) return
    if (latitude && longitude) {
      dispatch(fetchPlaceByGeoCoords(latitude, longitude, { levels }))
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [placeKey, disable])

  return [disable ? undefined : (place ?? undefined), fetching]
}

export function usePlaceByName(name: string | undefined): App.Place | undefined {
  const place = useAppSelector(state => state.destination.locationToPlace[name ?? ''])
  const dispatch = useAppDispatch()

  useEffect(() => {
    if (name) {
      dispatch(fetchPlaceByName(name))
    }
  }, [name, dispatch])
  return place ?? undefined
}

export function usePlacesAncestorsById(placeIds: Array<string>): [Array<Array<string>>, Array<boolean>] {
  const ancestorIdsList = useAppSelector(state => state.destination.placeAncestors ?? EmptyArray)
  const fetching = useAppSelector(state => state.destination.fetchingAncestors)
  const dispatch = useAppDispatch()
  const normalizedIds = useMemo(
    () => Array.from(new Set(placeIds.filter(Boolean))),
    [placeIds],
  )
  const requestIds = useStableObjectReference(normalizedIds)
  const ancestorsList: Array<Array<string>> = useMemo(() => placeIds.map(id => ancestorIdsList[id] ?? []), [placeIds, ancestorIdsList])
  const fetchingList = useMemo<Array<boolean>>(() => placeIds.map(id => !!fetching[id]), [placeIds, fetching])
  useEffect(() => {
    for (const placeId of requestIds) {
      dispatch(fetchPlaceAncestorsById(placeId))
    }
  }, [requestIds, dispatch])

  return [ancestorsList, fetchingList]
}

export function usePlaceAncestorsById(placeId = ''): [Array<string>, boolean] {
  const placeIdList = useMemo(() => [placeId], [placeId])
  const [ancestorIdsList, fetchingList] = usePlacesAncestorsById(placeIdList)
  const [firstAncestor, firstFetching] = [ancestorIdsList[0], fetchingList[0]]
  return [firstAncestor, firstFetching]
}

interface PlaceBySearchPhraseOptions {
  placeTypes?: Array<App.SearchPlaceType>
  searchTypes?: Array<App.SearchType>
  vertical?: App.SearchPlaceVertical
  disabled?: boolean
  priority?: App.SearchPlacePriority
  limit?: number
  canViewLuxPlusBenefits?: boolean
  rollout?: Array<string>
}

const EmptyObject = {}
export function usePlacesBySearchPhrase(
  phrase: string,
  options: PlaceBySearchPhraseOptions = EmptyObject,
): [Array<App.SearchItem>, boolean, boolean] {
  const { currentRegionCode } = useContext(GeoContext)
  const [fetching, setFetching] = useState<boolean>(true)
  const [error, setError] = useState<boolean>(false)
  const [results, setResults] = useState<Array<App.SearchItem>>([])
  const optionsRef = useRef<PlaceBySearchPhraseOptions>(options)
  optionsRef.current = options

  const getDebouncedPredictions = useMemo(
    () =>
      debounce((searchPhrase: string) => {
        if (searchPhrase.length >= 2) {
          return getTypeaheadSuggestions(searchPhrase, currentRegionCode, {
            types: optionsRef.current.placeTypes,
            searchTypes: optionsRef.current.searchTypes,
            vertical: optionsRef.current.vertical,
            priority: optionsRef.current.priority,
            limit: optionsRef.current.limit,
            canViewLuxPlusBenefits: optionsRef.current.canViewLuxPlusBenefits,
            rollout: optionsRef.current.rollout,
          })
            .then((searchItems) => {
              setResults(searchItems)
              setError(false)
            })
            .catch(() => setError(true))
            .finally(() => setFetching(false))
        } else {
          // search not long enough, means no results, so return empty result
          setFetching(false)
          return Promise.resolve([])
        }
      }, 200),
    [currentRegionCode],
  )

  useEffect(() => {
    if (!options.disabled) {
      setFetching(true)
      getDebouncedPredictions(phrase)
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [phrase, options.disabled])

  const localizedResults = useLocalizedSearchItems(results)

  return [localizedResults, fetching, error]
}
